import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Hash, TrendingUp, Plus, Edit2, Trash2, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

const SOURCES = ['google-ads','meta','justdial','website','hoarding','referral','walk-in','other'];

export default function TrackedNumbersPage({ API_BASE }) {
  const [numbers, setNumbers] = useState([]);
  const [roi, setRoi] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API_BASE}/tracked-numbers`),
      axios.get(`${API_BASE}/tracked-numbers/roi`),
    ]);
    setNumbers(a.data || []); setRoi(b.data || []);
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Remove this tracked number?')) return;
    await axios.delete(`${API_BASE}/tracked-numbers/${id}`); load();
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>📲 Call Tracking — Marketing Numbers</h2>
        <button onClick={()=>{ setEditing(null); setShowForm(true); }} style={btn(theme.primary)}><Plus size={14}/> Add Number</button>
      </div>

      <div style={{ ...cardStyle, padding: 14, marginBottom: 18, background: theme.secondaryLight }}>
        <div style={{ fontWeight: 700, color: theme.secondaryDark, fontSize: 14 }}>💡 Kaise kaam karta hai?</div>
        <div style={{ fontSize: 12.5, color: theme.navy, marginTop: 6, lineHeight: 1.6 }}>
          Har marketing channel ke liye alag phone number lo (Exotel, Knowlarity, Twilio se), aur uska webhook ye URL pe point karo:
          <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, marginLeft: 6 }}>{API_BASE}/calls/webhook</code>.
          Phir har incoming call automatically uske channel me tag ho jaayegi — kaunsa ad/source patient laaya, ye saaf dikhega.
        </div>
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: theme.primaryLight }}>
            <tr>
              <th style={th}>Label</th><th style={th}>Number</th><th style={th}>Source / Campaign</th>
              <th style={th}>Calls</th><th style={th}>Booked</th><th style={th}>Conv %</th>
              <th style={th}>Spend / mo</th><th style={th}>Cost / Booking</th><th style={th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {numbers.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>Koi tracked number nahi. <Hash size={14}/> "Add Number" se start karo.</td></tr>
            )}
            {numbers.map(n => {
              const r = roi.find(x => x._id === n._id) || {};
              return (
                <tr key={n._id} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                  <td style={td}><strong>{n.label}</strong>{!n.active && <span style={{ marginLeft: 6, fontSize: 10, color: theme.danger }}>INACTIVE</span>}</td>
                  <td style={td}><code>{n.number}</code></td>
                  <td style={td}>{n.source} {n.campaign && <span style={{ color: theme.textMuted }}>· {n.campaign}</span>}</td>
                  <td style={td}>{r.totalCalls || 0}</td>
                  <td style={td}>{r.booked || 0}</td>
                  <td style={td}><strong style={{ color: (r.conversionRate||0) > 30 ? theme.success : theme.warning }}>{r.conversionRate||0}%</strong></td>
                  <td style={td}>₹{(n.spendMonthly||0).toLocaleString('en-IN')}</td>
                  <td style={td}><strong>₹{(r.costPerBooking||0).toLocaleString('en-IN')}</strong></td>
                  <td style={td}>
                    <button onClick={()=>{ setEditing(n); setShowForm(true); }} style={iconBtn}><Edit2 size={14}/></button>
                    <button onClick={()=>remove(n._id)} style={{ ...iconBtn, color: theme.danger }}><Trash2 size={14}/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && <NumberForm API_BASE={API_BASE} initial={editing} onClose={()=>{ setShowForm(false); setEditing(null); }} onSaved={()=>{ setShowForm(false); setEditing(null); load(); }}/>}
    </div>
  );
}

function NumberForm({ API_BASE, initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || { label: '', number: '', source: 'google-ads', campaign: '', forwardTo: '', spendMonthly: 0, active: true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (initial) await axios.put(`${API_BASE}/tracked-numbers/${initial._id}`, f);
      else         await axios.post(`${API_BASE}/tracked-numbers`, f);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };
  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding: 24, width: 'min(520px, 92vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>{initial ? 'Edit Tracked Number' : 'New Tracked Number'}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Label</label>
          <input value={f.label} onChange={e=>setF({...f, label:e.target.value})} style={inputStyle} placeholder="Google Ads — Knee Implant Camp"/>
          <label style={labelStyle}>Number</label>
          <input value={f.number} onChange={e=>setF({...f, number:e.target.value})} style={inputStyle} placeholder="+91 98765 43210"/>
          <label style={labelStyle}>Source</label>
          <select value={f.source} onChange={e=>setF({...f, source:e.target.value})} style={inputStyle}>
            {SOURCES.map(s=><option key={s}>{s}</option>)}
          </select>
          <label style={labelStyle}>Campaign (optional)</label>
          <input value={f.campaign} onChange={e=>setF({...f, campaign:e.target.value})} style={inputStyle}/>
          <label style={labelStyle}>Forward To (reception number)</label>
          <input value={f.forwardTo} onChange={e=>setF({...f, forwardTo:e.target.value})} style={inputStyle}/>
          <label style={labelStyle}>Monthly Spend (₹)</label>
          <input type="number" value={f.spendMonthly} onChange={e=>setF({...f, spendMonthly:Number(e.target.value)})} style={inputStyle}/>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <input type="checkbox" checked={f.active} onChange={e=>setF({...f, active:e.target.checked})}/> Active
          </label>
        </div>
        <button onClick={save} disabled={busy} style={{ ...btn(theme.primary), marginTop: 14, width: '100%' }}>{busy ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 12, color: theme.navy, fontWeight: 700 };
const td = { padding: '10px 12px', fontSize: 13 };
const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const iconBtn = { background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: theme.navy };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
